import { DEPTH_SLAB, DRONE_MAX_Z, WATER_Y, WORLD_W, type KamikazeInput, type Terrain } from '@tanky/shared';

/** Výdrž baterie (s). */
export const DRONE_BATTERY = 20;
const SPEED = 150; // px/s dopředu
const STRAFE = 110;
const CLIMB = 100;
const TURN = 1.8; // rad/s
/** Jak rychle se rychlost přiblíží požadované (setrvačnost). */
const RESPONSE = 3;

/** Stejné osy jako kamikadze dron. */
export type DroneInput = KamikazeInput;

export type DroneEnd = 'crash' | 'water' | 'battery' | 'return';

/**
 * Průzkumný dron – čistě na klientu, nic nemění ve stavu hry.
 * Souřadnice jako 2D svět (x doprava, y dolů) + z do hloubky; yaw 0 = směr +x.
 */
export class DroneFlight {
  x: number;
  y: number;
  z = 0;
  yaw: number;
  /** Náklon pro kameru (rad) – jen vizuální. */
  pitch = 0;
  roll = 0;
  battery = DRONE_BATTERY;
  ended: DroneEnd | null = null;
  private vx = 0;
  private vy = 0;
  private vz = 0;
  readonly input: DroneInput = { throttle: 0, yaw: 0, forward: 0, strafe: 0 };

  constructor(
    private terrain: Terrain,
    x: number,
    y: number,
    facingRight: boolean,
    private wind: number,
  ) {
    this.x = x;
    this.y = y - 26;
    this.yaw = facingRight ? 0 : Math.PI;
  }

  update(dt: number) {
    if (this.ended) return;
    this.battery -= dt;
    if (this.battery <= 0) {
      this.battery = 0;
      this.ended = 'battery';
      return;
    }
    const inp = this.input;
    this.yaw += -inp.yaw * TURN * dt;
    const fx = Math.cos(this.yaw);
    const fz = -Math.sin(this.yaw);
    // Požadovaná rychlost ve světě (y dolů → plyn nahoru = záporné vy)
    const tx = fx * inp.forward * SPEED + -fz * inp.strafe * STRAFE + this.wind * 0.4;
    const tz = fz * inp.forward * SPEED + fx * inp.strafe * STRAFE;
    const ty = -inp.throttle * CLIMB;
    const k = 1 - Math.exp(-dt * RESPONSE);
    this.vx += (tx - this.vx) * k;
    this.vy += (ty - this.vy) * k;
    this.vz += (tz - this.vz) * k;
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.z += this.vz * dt;
    this.x = Math.max(-300, Math.min(WORLD_W + 300, this.x));
    this.y = Math.max(-400, this.y);
    this.z = Math.max(-DRONE_MAX_Z, Math.min(DRONE_MAX_Z, this.z));
    // Náklon kamery podle pohybu, jako u skutečného dronu
    this.pitch += (-inp.forward * 0.18 - 0.12 - this.pitch) * k;
    this.roll += (inp.strafe * 0.25 + inp.yaw * 0.1 - this.roll) * k;

    if (this.y >= WATER_Y) this.ended = 'water';
    else if (Math.abs(this.z) < DEPTH_SLAB && this.terrain.solid(this.x, this.y)) this.ended = 'crash';
  }
}
